import React from 'react'

export default function MaxPriceTooltip({
  priceConfig,
  losCategories,
  paxCategories,
  otaChannels,
  pricingRules,
  suggestedMaxPrice,
}: {
  priceConfig: any
  losCategories: any[]
  paxCategories: any[]
  otaChannels: any[]
  pricingRules: any[]
  suggestedMaxPrice: { regular: number; specialDay: number }
}) {
  // Helper to build steps for a given scenario
  function buildSteps({ useSpecialDay }: { useSpecialDay: boolean }) {
    let steps = []
    let subtotal = priceConfig.basePrice
    steps.push({
      label: 'Base Price',
      percent: '',
      amount: '',
      subtotal: `€${subtotal.toFixed(2)}`,
    })
    // Buffer (immediately after base price)
    let bufferPercent = priceConfig.bufferPercent ?? 20
    let bufferAmount = subtotal * (bufferPercent / 100)
    subtotal += bufferAmount
    steps.push({
      label: `Buffer (${bufferPercent}%)`,
      percent: bufferPercent ? `+${bufferPercent}%` : '',
      amount: bufferPercent ? `+€${bufferAmount.toFixed(2)}` : '',
      subtotal: `€${subtotal.toFixed(2)}`,
    })
    // Max LOS Modifier
    let maxLOS = losCategories.reduce((max, los) => {
      if (los.sellingPriceDifferenceType === 'percent') {
        return (los.sellingPriceDifference ?? 0) >
          (max.sellingPriceDifference ?? 0)
          ? los
          : max
      }
      return max
    }, losCategories[0] || { sellingPriceDifference: 0, sellingPriceDifferenceType: 'percent' })
    let losPercent =
      maxLOS?.sellingPriceDifferenceType === 'percent'
        ? maxLOS.sellingPriceDifference ?? 0
        : 0
    let losAmount = subtotal * (losPercent / 100)
    subtotal += losAmount
    steps.push({
      label: 'Max LOS Modifier',
      percent: losPercent ? `+${losPercent}%` : '',
      amount: losPercent ? `+€${losAmount.toFixed(2)}` : '',
      subtotal: `€${subtotal.toFixed(2)}`,
    })
    // Max PAX Modifier
    let maxPAX = paxCategories.reduce((max, pax) => {
      if (pax.sellingPriceDifferenceType === 'percent') {
        return (pax.sellingPriceDifference ?? 0) >
          (max.sellingPriceDifference ?? 0)
          ? pax
          : max
      }
      return max
    }, paxCategories[0] || { sellingPriceDifference: 0, sellingPriceDifferenceType: 'percent' })
    let paxPercent =
      maxPAX?.sellingPriceDifferenceType === 'percent'
        ? maxPAX.sellingPriceDifference ?? 0
        : 0
    let paxAmount = subtotal * (paxPercent / 100)
    subtotal += paxAmount
    steps.push({
      label: 'Max PAX Modifier',
      percent: paxPercent ? `+${paxPercent}%` : '',
      amount: paxPercent ? `+€${paxAmount.toFixed(2)}` : '',
      subtotal: `€${subtotal.toFixed(2)}`,
    })
    // Pricing Rules
    const rules = pricingRules.filter(
      (rule) => rule.enabled && rule.priceModifier > 0,
    )
    // Special day rules: dateRangeFilter, no dayOfWeek/month, and name includes 'holiday' or 'special'
    const specialDayRules = rules.filter(
      (rule) =>
        rule.dateRangeFilter &&
        !rule.dayOfWeekFilter &&
        !rule.monthFilter &&
        (rule.name.toLowerCase().includes('holiday') ||
          rule.name.toLowerCase().includes('special')),
    )
    // Season rules: dateRangeFilter or monthFilter, but NOT special day rules
    const seasonRules = rules.filter(
      (rule) =>
        (rule.dateRangeFilter || rule.monthFilter) &&
        !(
          rule.name.toLowerCase().includes('holiday') ||
          rule.name.toLowerCase().includes('special')
        ),
    )
    // Repeating day rules: dayOfWeekFilter, not dateRange/month, and NOT special day rules
    const repeatingDayRules = rules.filter(
      (rule) =>
        rule.dayOfWeekFilter &&
        !rule.dateRangeFilter &&
        !rule.monthFilter &&
        !(
          rule.name.toLowerCase().includes('holiday') ||
          rule.name.toLowerCase().includes('special')
        ),
    )
    if (useSpecialDay && specialDayRules.length > 0) {
      // Only use the highest special day rule, do not compound with others
      let maxSpecialDayRule = specialDayRules.reduce((a, b) =>
        a.priceModifier > b.priceModifier ? a : b,
      )
      let specialAmount = subtotal * (maxSpecialDayRule.priceModifier / 100)
      subtotal += specialAmount
      steps.push({
        label: `Special Day Rule (${maxSpecialDayRule.name})`,
        percent: `+${maxSpecialDayRule.priceModifier}%`,
        amount: `+€${specialAmount.toFixed(2)}`,
        subtotal: `€${subtotal.toFixed(2)}`,
      })
    } else {
      // Highest season rule
      let maxSeasonRule =
        seasonRules.length > 0
          ? seasonRules.reduce((a, b) =>
              a.priceModifier > b.priceModifier ? a : b,
            )
          : undefined
      if (maxSeasonRule) {
        let seasonAmount = subtotal * (maxSeasonRule.priceModifier / 100)
        subtotal += seasonAmount
        steps.push({
          label: `Season Rule (${maxSeasonRule.name})`,
          percent: `+${maxSeasonRule.priceModifier}%`,
          amount: `+€${seasonAmount.toFixed(2)}`,
          subtotal: `€${subtotal.toFixed(2)}`,
        })
      }
      // Highest repeating day rule
      let maxRepeatingDayRule =
        repeatingDayRules.length > 0
          ? repeatingDayRules.reduce((a, b) =>
              a.priceModifier > b.priceModifier ? a : b,
            )
          : undefined
      if (maxRepeatingDayRule) {
        let repeatAmount = subtotal * (maxRepeatingDayRule.priceModifier / 100)
        subtotal += repeatAmount
        steps.push({
          label: `Repeating Day Rule (${maxRepeatingDayRule.name})`,
          percent: `+${maxRepeatingDayRule.priceModifier}%`,
          amount: `+€${repeatAmount.toFixed(2)}`,
          subtotal: `€${subtotal.toFixed(2)}`,
        })
      }
    }
    // Max Channel Margin
    let maxChannelMargin = Math.max(
      ...otaChannels.filter((ch) => ch.enabled).map((ch) => ch.marginPercent),
    )
    let channelAmount = subtotal * (maxChannelMargin / 100)
    subtotal += channelAmount
    steps.push({
      label: 'Max Channel Margin',
      percent: maxChannelMargin ? `+${maxChannelMargin}%` : '',
      amount: maxChannelMargin ? `+€${channelAmount.toFixed(2)}` : '',
      subtotal: `€${subtotal.toFixed(2)}`,
    })
    return steps
  }
  // Build both breakdowns
  const regularSteps = buildSteps({ useSpecialDay: false })
  const specialDaySteps = buildSteps({ useSpecialDay: true })
  return (
    <div className="space-y-6">
      <div>
        <div className="font-semibold mb-1">Regular Max Calculation</div>
        <table className="w-full text-xs mb-2">
          <thead>
            <tr className="text-gray-500">
              <th className="text-left font-normal">Step</th>
              <th className="text-right font-normal w-20">%</th>
              <th className="text-right font-normal w-28">Δ</th>
              <th className="text-right font-normal">Subtotal</th>
            </tr>
          </thead>
          <tbody>
            {regularSteps.map((step, i) => (
              <tr key={i}>
                <td className="pr-2">{step.label}</td>
                <td className="text-right pr-2">{step.percent}</td>
                <td className="text-right pr-2">{step.amount}</td>
                <td className="text-right">{step.subtotal}</td>
              </tr>
            ))}
            <tr className="border-t border-gray-300">
              <td colSpan={3} className="font-bold text-right pr-2 pt-2">
                Suggested Max:
              </td>
              <td className="font-bold text-primary-600 text-right pt-2">
                €{suggestedMaxPrice?.regular.toFixed(2)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div>
        <div className="font-semibold mb-1">Special Day Max Calculation</div>
        <table className="w-full text-xs">
          <thead>
            <tr className="text-gray-500">
              <th className="text-left font-normal">Step</th>
              <th className="text-right font-normal w-20">%</th>
              <th className="text-right font-normal w-28">Δ</th>
              <th className="text-right font-normal">Subtotal</th>
            </tr>
          </thead>
          <tbody>
            {specialDaySteps.map((step, i) => (
              <tr key={i}>
                <td className="pr-2">{step.label}</td>
                <td className="text-right pr-2">{step.percent}</td>
                <td className="text-right pr-2">{step.amount}</td>
                <td className="text-right">{step.subtotal}</td>
              </tr>
            ))}
            <tr className="border-t border-gray-300">
              <td colSpan={3} className="font-bold text-right pr-2 pt-2">
                Special Day Max:
              </td>
              <td className="font-bold text-pink-600 text-right pt-2">
                €{suggestedMaxPrice?.specialDay.toFixed(2)}
              </td>
            </tr>
          </tbody>
        </table>
        <div className="text-xs text-gray-500 mt-1">
          Special day rules are not compounded with any other date-based price
          rule.
        </div>
      </div>
    </div>
  )
}
